/**
 * Shared Tree Transform Helpers
 * Common functions used by Caster, Roof, and Sidewall tree transforms.
 */

import type { EquipmentTreeRowFlat } from "../models";

// ============================================
// Types
// ============================================

export interface ExpectedChildPosition {
  positionId: number;
  positionName: string;
  partTypeId: number;
  partTypeName: string;
}

// ============================================
// Helper Functions
// ============================================

/**
 * Sort positions by L1PositionId ascending and deduplicate by positionId.
 * Generic — works with any row type that has L1PositionId.
 */
export const sortAndDeduplicatePositions = <
  T extends { L1PositionId: number | null },
>(
  positions: T[],
): T[] => {
  const seen = new Set<number>();
  return positions
    .filter((p) => p.L1PositionId != null && p.L1PositionId > 0)
    .sort((a, b) => (a.L1PositionId ?? 0) - (b.L1PositionId ?? 0))
    .filter((p) => {
      if (seen.has(p.L1PositionId!)) return false;
      seen.add(p.L1PositionId!);
      return true;
    });
};

/**
 * Build a map of spares container IDs to their spare parts.
 * Deduplicates by L2PartId per container to avoid duplicate rows.
 *
 * @param apiData - Raw API rows
 * @param sparesContainerIds - Container IDs to look for
 */
export const buildSparePartsMap = <
  T extends { L1PartId: number; L2PartId: number | null },
>(
  apiData: T[],
  sparesContainerIds: number[],
): Map<number, T[]> => {
  const map = new Map<number, T[]>();
  const seenPartIds = new Map<number, Set<number>>();

  for (const row of apiData) {
    if (
      row.L1PartId &&
      sparesContainerIds.includes(row.L1PartId) &&
      row.L2PartId
    ) {
      let containerParts = map.get(row.L1PartId);
      let containerSeen = seenPartIds.get(row.L1PartId);

      if (!containerParts || !containerSeen) {
        containerParts = [];
        containerSeen = new Set();
        map.set(row.L1PartId, containerParts);
        seenPartIds.set(row.L1PartId, containerSeen);
      }

      if (containerSeen.has(row.L2PartId)) continue;
      containerSeen.add(row.L2PartId);
      containerParts.push(row);
    }
  }

  return map;
};

/**
 * Parse expected child positions from comma-separated fields returned by SP.
 * Used by Caster (groups, foot rolls) and Sidewall (burner sub-components).
 */
export const parseExpectedChildPositions = (
  positionIds: string | null | undefined,
  positionNames: string | null | undefined,
  partTypeIds: string | null | undefined,
  partTypeNames: string | null | undefined,
): ExpectedChildPosition[] => {
  if (!positionIds || !positionNames || !partTypeIds || !partTypeNames)
    return [];

  const ids = positionIds.split(",").map(Number);
  const names = positionNames.split(",");
  const typeIds = partTypeIds.split(",").map(Number);
  const typeNames = partTypeNames.split(",");

  if (
    ids.length !== names.length ||
    ids.length !== typeIds.length ||
    ids.length !== typeNames.length
  ) {
    return [];
  }

  return ids.map((id, index) => ({
    positionId: id,
    positionName: names[index],
    partTypeId: typeIds[index],
    partTypeName: typeNames[index],
  }));
};

/**
 * Create a container row (Online/Offline/Spares containers).
 * Generic — returns the domain-specific row type via type assertion.
 * Safe because all domain types extend EquipmentTreeRowFlat with optional fields only.
 */
export const createContainerRow = <T extends EquipmentTreeRowFlat>(
  containerId: number,
  containerName: string,
  path: string[],
  rowType: "container" | "sparesContainer",
): T =>
  ({
    id: `container-${containerId}`,
    path,
    rowType,
    ContainerId: containerId,
    ContainerName: containerName,
    EquipmentId: containerId,
    Heats: 0,
    InstalledDate: null,
  }) as T;
